
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SubscriptionState } from "@/types/subscription";

export const useSubscription = () => {
  const [state, setState] = useState<SubscriptionState>({
    isLoading: false,
    selectedTier: null,
  });
  const { toast } = useToast();

  const handleSubscribe = async (priceId: string | null) => {
    try {
      setState({ isLoading: true, selectedTier: priceId });
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Handle free tier selection
      if (priceId === null) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            subscription_tier_id: (
              await supabase
                .from('subscription_tiers')
                .select('id')
                .eq('name', 'Free')
                .single()
            ).data?.id,
            subscription_status: 'free',
            trial_started_at: new Date().toISOString()
          })
          .eq('id', user.id);

        if (updateError) throw updateError;

        toast({
          title: "Success",
          description: "You've been successfully enrolled in the Free tier",
        });
        return;
      }

      // Handle paid tiers through Stripe
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { tier: priceId }
      });

      if (error) {
        console.error('Checkout session error:', error);
        throw error;
      }
      if (!data?.url) {
        console.error('No checkout URL received from server');
        throw new Error('No checkout URL received');
      }

      window.location.href = data.url;
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast({
        title: "Error",
        description: error.message || 'Failed to process subscription request',
        variant: "destructive",
      });
    } finally {
      setState({ isLoading: false, selectedTier: null });
    }
  };

  const handleManageSubscription = async () => {
    try {
      setState({ ...state, isLoading: true });
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('create-portal-session', {});

      if (error) {
        console.error('Portal session error:', error);
        throw error;
      }

      if (!data?.url) {
        throw new Error('No portal URL received');
      }

      window.location.href = data.url;
    } catch (error: any) {
      console.error('Portal session error:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setState({ ...state, isLoading: false });
    }
  };

  return {
    state,
    handleSubscribe,
    handleManageSubscription,
  };
};
